/**
 * The invoice pages' date and wording rules, kept apart from the pages so they can be tested without
 * a canvas.
 *
 * Due, issued and paid dates are CALENDAR dates: stored at UTC midnight and printed, never converted.
 * A line's date is a day in the reader's life, pinned to LOCAL noon so it survives the round trip in
 * every timezone. Filter bounds are local midnights, because "created on the 3rd" means the reader's
 * 3rd.
 */
import { InvoiceStatus, type InvoiceDto, type InvoiceEmailDto } from './contracts.gen.js';
import { localDateTime, parseInstant } from './format.js';

/** Local midnight of a `yyyy-MM-dd`, as an instant - plus `days`, for the exclusive upper bound. */
export function localMidnightUtc(iso: string, days = 0): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days).toISOString();
}

/** A `yyyy-MM-dd` as UTC midnight: how a due date is stored, so it prints without a timezone shift. */
export function calendarUtc(iso: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00Z` : null;
}

/** A `yyyy-MM-dd` as LOCAL noon, so a line's day survives the round trip in every timezone. */
export function localNoonUtc(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12).toISOString() : null;
}

/** The local calendar day of an instant, as `yyyy-MM-dd`. */
export function localIsoOf(value: string | null | undefined): string {
  const d = parseInstant(value);
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function todayLocalIso(): string {
  return localIsoOf(new Date().toISOString());
}

export function isEditable(i: InvoiceDto | null): boolean {
  return i !== null && (i.status === InvoiceStatus.Draft || i.status === InvoiceStatus.Issued);
}

/** The one-line answer to "has this been sent?" - see the header comment for why a failure wins. */
export function lastSentLine(history: readonly InvoiceEmailDto[]): string {
  const latest = history[0];
  if (!latest) return 'Not emailed yet.';
  const when = localDateTime(latest.sentUtc);
  if (!latest.success) return `Last attempt ${when} failed: ${latest.error}`;
  if (latest.bounced) return `Sent ${when} to ${latest.to} - delivery failed: ${latest.bounceDetail}`;
  const replies =
    latest.replyCount === 1 ? ' · 1 reply' : latest.replyCount > 1 ? ` · ${latest.replyCount} replies` : '';
  return `Emailed ${when} to ${latest.to}${replies}`;
}

